import { useState, useEffect } from "react";
import type { ComparisonTypes } from "../types/ComparisonTypes";
import "../styles/InsuranceComparison.css";
import "../styles/ComparisonStyles.css";
import InsuranceInfo from "./InsuranceInfo";
import DifferenceHighlighter from "./DifferenceHighlighter";
import CategoryFilter from "./CategoryFilter";
import { getDifferencesBySection } from "../utils/compareInsurance";

// Available companies for selection
const AVAILABLE_COMPANIES = [
    "Fremtind",
    "KLP",
    "IF",
    "Gjensidige",
    "Storebrand",
    "Frende",
    "JBF",
    "Knif",
    "Landkreditt",
    "Tryg",
    "Instabank",
];

// Available coverage levels for selection
const COVERAGE_LEVELS = [
    "Ansvar",
    "Delkasko",
    "Kasko",
    "Utvidet"
];

// Interface for company data
interface CompanyData {
    name: string;
    data: ComparisonTypes[];
}

const InsuranceComparison: React.FC = () => {
    const [selectedCompanies, setSelectedCompanies] = useState<string[]>([
        "Fremtind",
        "KLP",
        "IF",
    ]);
    const [selectedCoverageLevel, setSelectedCoverageLevel] = useState<string>("Utvidet");
    const [companiesData, setCompaniesData] = useState<CompanyData[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedCategory, setSelectedCategory] = useState<string>("all");

    // Function to handle company selection change
    const handleCompanySelection = (company: string) => {
        // Toggle company selection (add or remove)
        setSelectedCompanies((prev) => {
            // If already selected, remove it
            if (prev.includes(company)) {
                return prev.filter((c) => c !== company);
            }
            // If we already have 3 companies, replace the last one
            if (prev.length >= 3) {
                return [...prev.slice(0, 2), company];
            }
            // Otherwise add the new company
            return [...prev, company];
        });
    };

    // Function to handle coverage level selection change
    const handleCoverageLevelSelection = (level: string) => {
        setSelectedCoverageLevel(level);
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                // Create an array of promises to fetch data for each selected company
                const companyPromises = selectedCompanies.map((company) =>
                    fetch(`/data/${company}/${selectedCoverageLevel}/comparison_result.json`)
                        .then((response) => {
                            if (!response.ok) {
                                // Fall back to the root directory if coverage level folder doesn't exist
                                return fetch(`/data/${company}/comparison_result.json`);
                            }
                            return response;
                        })
                        .then((response) => {
                            if (!response.ok) {
                                throw new Error(
                                    `HTTP error! Status: ${response.status} for ${company}`,
                                );
                            }
                            return response.json();
                        })
                        .then((data) => ({
                            name: company,
                            data,
                        })),
                );

                // Wait for all promises to resolve
                const results = await Promise.all(companyPromises);

                // Update state with all company data
                setCompaniesData(results);
                setLoading(false);
            } catch (error) {
                console.error("Error fetching comparison data:", error);
                setError(
                    "Failed to load insurance comparison data. Please try again later.",
                );
                setLoading(false);
            }
        };

        fetchData();
    }, [selectedCompanies, selectedCoverageLevel]); // Re-fetch when selected companies or coverage level changes

    // Check if there are any differences in a specific category
    const hasDifferencesInCategory = (category: string): boolean => {
        if (companiesData.length <= 1 || category === "all") return false;

        const insurances = companiesData
            .map((company) =>
                company.data && company.data.length > 0 ? company.data[0] : null,
            )
            .filter(Boolean) as ComparisonTypes[];

        if (insurances.length <= 1) return false;

        // For each section in the category, check if there are differences
        if (category in insurances[0]) {
            const differences = getDifferencesBySection(
                insurances,
                category as keyof ComparisonTypes,
            );
            return Object.values(differences).some((hasDiff) => hasDiff);
        }

        return false;
    };

    // Handle category change
    const handleCategoryChange = (category: string) => {
        setSelectedCategory(category);
    };

    if (error) {
        return (
            <div className="error-container">
                <div className="error-message">
                    <h2>Error</h2>
                    <p>{error}</p>
                    <button type="button" onClick={() => window.location.reload()}>
                        Refresh Page
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="insurance-comparison">
            {/* Loading overlay */}
            {loading && (
                <div className="loading-overlay">
                    <div className="loading-spinner" />
                </div>
            )}

            <div className="comparison-header">
                <h1>Forsikringssammenligning</h1>
                <p>Sammenlign forsikringstilbud</p>

                {/* Company selection UI */}
                <div className="company-selection">
                    <h3>Velg selskaper å sammenligne (maks 3)</h3>
                    <div className="selection-count">
                        <span className="count-badge">
                            {selectedCompanies.length} av 3 selskaper valgt
                        </span>
                    </div>
                    <div className="company-buttons">
                        {AVAILABLE_COMPANIES.map((company) => (
                            <button
                                type="button"
                                key={company}
                                className={`company-button ${selectedCompanies.includes(company) ? "selected" : ""}`}
                                onClick={() => handleCompanySelection(company)}
                                disabled={
                                    (loading && !selectedCompanies.includes(company)) ||
                                    (selectedCompanies.length >= 3 &&
                                        !selectedCompanies.includes(company))
                                }
                            >
                                {company}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Coverage level selection UI */}
                <div className="coverage-level-selection">
                    <h3>Velg dekningsgrad</h3>
                    <div className="coverage-buttons">
                        {COVERAGE_LEVELS.map((level) => (
                            <button
                                type="button"
                                key={level}
                                className={`coverage-button ${selectedCoverageLevel === level ? "selected" : ""}`}
                                onClick={() => handleCoverageLevelSelection(level)}
                                disabled={loading}
                            >
                                {level}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Category filter UI */}
                {!loading && companiesData.length > 0 && (
                    <CategoryFilter
                        selectedCategory={selectedCategory}
                        onCategoryChange={handleCategoryChange}
                        hasDifferencesInCategory={hasDifferencesInCategory}
                    />
                )}
            </div>

            <div className="selected-companies-info">
                <h3>Sammenligner {selectedCompanies.join(", ")} - {selectedCoverageLevel}</h3>
            </div>


            <div className="comparison-container">
                {/* Render columns for each selected company */}
                {companiesData.map((companyData) => (
                    <div key={companyData.name} className="insurance-column">
                        {companyData.data && companyData.data.length > 0 ? (
                            companyData.data.map((insurance: ComparisonTypes) => (
                                <div
                                    key={`${companyData.name}-${insurance.selskap}`}
                                    className="insurance-card"
                                >
                                    <h2>
                                        {insurance.selskap} - {insurance.produkt}
                                    </h2>
                                    <p className="coverage-level">
                                        <strong>Dekningsgrad:</strong>
                                        <span className="highlight-value">
                                            {" "}
                                            {insurance.dekningsgrad_data.dekningsgrad}{" "}
                                        </span>
                                        <span className="level-indicator">
                                            (Nivå: {insurance.dekningsgrad_data.dekningsgrad_nivå})
                                        </span>
                                    </p>
                                    <InsuranceInfo
                                        insurance={insurance}
                                        selectedCategory={selectedCategory}
                                    />
                                </div>
                            ))
                        ) : (
                            <div className="insurance-card missing-data">
                                <h2>{companyData.name}</h2>
                                <p>Ingen data tilgjengelig for dette selskapet enda.</p>
                            </div>
                        )}
                    </div>
                ))}

                {/* Show empty columns for remaining slots */}
                {Array.from({ length: 3 - selectedCompanies.length }).map(
                    (_, index) => {
                        // Calculate position based on existing companies length and this index
                        const position = selectedCompanies.length + index + 1;
                        return (
                            <div key={`empty-column-${position}`} className="empty-column">
                                <div className="empty-card">
                                    <h3>Velg et selskap</h3>
                                    <p>Velg et selskap fra listen over for å sammenligne</p>
                                </div>
                            </div>
                        );
                    },
                )}
            </div>
            {/* Show difference highlighter when we have data from multiple companies */}
            {companiesData.length > 1 &&
                companiesData.every((c) => c.data && c.data.length > 0) && (
                    <DifferenceHighlighter
                        companies={companiesData.map((c) => c.data[0])}
                    />
                )}
        </div>
    );
};

export default InsuranceComparison;
